import { ViewType, ViewObject } from '../base';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceType, PriceInfo } from '@unpispas/upp-defs';
import { dataService } from '../../data';
import { TicketExtra } from '../objects/ticketextra';

import { TicketView } from './ticket';
import { ExtraView } from './extra';

export interface TicketExtraInfo {
    charge: number,         // total amount to be charged for the ticket extra
    refund: number          // total amount to be refunded for the ticket extra
}

/****************************/
/* CLASS VIEW               */
/****************************/

export class _TicketExtraView extends ViewObject<TicketExtra> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get ticketextra(): TicketExtra{
        return this.object;
    }
             
    constructor(object: TicketExtra, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }


    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/
    
    private _calcextrainfo: TicketExtraInfo | null = null;
    private _calcpriceinfo: PriceType | null = null;    // final price for discount (charge - refund)
    private _calcchargeinfo: PriceType | null = null;   // charge price for discount    
    private _calcrefundinfo: PriceType | null = null;   // refund price for discount

    private RefreshView(targets: string[]){
        if (targets.includes('TICKETEXTRA')){
            this._calcextrainfo = null;
            this._calcpriceinfo = null;
            this._calcchargeinfo = null;
            this._calcrefundinfo = null;            
            this.DoRefreshView();
        }

        if (targets.includes('TICKETPRODUCT')){
            this._calcextrainfo = null;
            this._calcpriceinfo = null;
            this._calcchargeinfo = null;
            this._calcrefundinfo = null;            
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];
        
        // register to updates on contained items          
        this._subscriptions.push(
            this.ticketextra.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        );    

        if (this.ticket){
            this._subscriptions.push(
                this.ticket.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })
            );
        }
    }
    
    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/   
    
    get ticket(): TicketView | null {
        return this.alives.get(this.ticketextra.ticket) as TicketView;
    }
    
    get extra(): ExtraView | null {
        return this.alives.get(this.ticketextra.extra) as ExtraView;
    }
    
    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/
    
    get priceinfo(): PriceType {
        if (this._calcpriceinfo === null){
            const _p:number = this.calcinfo.charge - this.calcinfo.refund;
            const _r:number = this.ticketextra.taxrate ?? AppConstants.defaultTaxRate;

            this._calcpriceinfo = new PriceInfo(_p, _r).price;       
        }

        return this._calcpriceinfo;
    }

    get chargeinfo(): PriceType {
        if (this._calcchargeinfo === null){
            const _p:number = this.calcinfo.charge;
            const _r:number = this.ticketextra.taxrate ?? AppConstants.defaultTaxRate;

            this._calcchargeinfo = new PriceInfo(_p, _r).price;       
        }

        return this._calcchargeinfo;
    }

    get refundinfo(): PriceType {
        if (this._calcrefundinfo === null){
            const _p:number = this.calcinfo.refund;
            const _r:number = this.ticketextra.taxrate ?? AppConstants.defaultTaxRate;

            this._calcrefundinfo = new PriceInfo(_p, _r).price;       
        }

        return this._calcrefundinfo;
    }

    get calcinfo(): TicketExtraInfo {   
        if (this._calcextrainfo === null){
            let _charge = 0;
            let _refund = 0;
    
            if (this.ticket && this.extra){
                _charge = (this.ticketextra.status == 'UN') ? 0: this.extra.ChargeAmount(this.ticket);
                _refund = (this.ticketextra.status == 'UN') ? 0: this.extra.RefundAmount(this.ticket);
            }
    
            this._calcextrainfo = {
                charge: _charge,
                refund: _refund
            };
        }

        return this._calcextrainfo;
    }

    /**
     * Stores the calculated charge on the applied extra.
     * Reserved for the ticket recalculation flow: reading `calcinfo` never
     * leaves the extra pending commit.
     */
    Recalculate(): void {
        const _calcinfo = this.calcinfo;
        this.ticketextra.charge = _calcinfo.charge - _calcinfo.refund;
        this._calcextrainfo = _calcinfo;       // the write above invalidates the cache
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type TicketExtraView = ViewType <_TicketExtraView, TicketExtra>;

export function TicketExtraViewProxy(object: TicketExtra, data: dataService): TicketExtraView {
    return new _TicketExtraView(object, data).Proxy as TicketExtraView;
}

